// ──────────────────────────────────────────────────────
// TrustModal — "ضمان الأصالة" modal shown when the TrustBadge is tapped
// (or when the AI chat surfaces the CTA).
//
// Phase 1: catalog-global authenticity layer. The modal frames this as
// PLATFORM / SUPPLIER-backed authenticity proof — it does NOT claim
// per-barcode verification. The copy is product-relevant ("هذا المنتج
// مشمول ضمن نظام ضمان الأصالة") without implying per-SKU tracing.
//
// Phase 2 — DOM architecture fix:
//   The product cards (HeroCard / BudgetCard / ProductCardLegacy) are
//   wrapped in Next.js <Link>, which renders as <a>. TrustBadge is nested
//   inside that anchor tree. When the badge-triggered modal rendered its
//   per-document "عرض الوثيقة" <a href="…"> links inline, we ended up with
//   <a>…<a>…</a>…</a> — illegal HTML, producing the React hydration
//   error "<a> cannot contain a nested <a>".
//
//   Fix: render the entire modal through React Portal into document.body
//   so its DOM lives OUTSIDE the card's anchor tree. The inner <a> PDF
//   links are now siblings of <body>, not descendants of the card's <a>.
//   SSR-safe: a `mounted` gate returns null until the component is on the
//   client so createPortal is never called during server render.
//
// Two body states, both intentionally premium:
//   A) No documents uploaded yet — shown as a "مستندات التوثيق قيد
//      التفعيل" panel. Reads like a finalisation step, NEVER like a
//      missing-setup / broken feature.
//   B) Active documents exist — listed with supplier / issuer / issue
//      date / optional brand-coverage chips and a clear preview action.
// ──────────────────────────────────────────────────────
'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    listPublicTrustDocuments,
    getPublicTrustFileUrl,
    type TrustDocument,
} from '@/lib/trust-api';

interface Props {
    onClose: () => void;
}

// Summary-block bullets — the three core claims, each rendered with an
// icon. Intentionally small and scannable; this is the first thing the
// customer reads inside the modal regardless of whether documents exist.
const SUMMARY_CLAIMS: { icon: string; text: string }[] = [
    { icon: '✅', text: 'منتج أصلي 100%' },
    { icon: '🌿', text: 'موردة عبر جهات معتمدة' },
    { icon: '🔐', text: 'مضمونة المصدر' },
];

export function TrustModal({ onClose }: Props) {
    // SSR / hydration gate: createPortal(..., document.body) cannot run
    // during server render because `document` doesn't exist there. We
    // track mount on the client and defer the portal call until after the
    // first client render, matching the canonical Next.js pattern for
    // document-targeted portals.
    const [mounted, setMounted] = useState(false);
    const [docs, setDocs] = useState<TrustDocument[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Mount gate for createPortal(..., document.body). The cascading
        // render this triggers is INTENTIONAL — it's how we flip from the
        // SSR-safe null render to the portal render. The project's
        // "react-you-might-not-need-an-effect" lint rule doesn't have a
        // carve-out for document-targeted portals, so suppress the warning
        // here with scope-explicit intent.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setMounted(true);
    }, []);

    useEffect(() => {
        let cancelled = false;
        listPublicTrustDocuments()
            .then((items) => { if (!cancelled) setDocs(items); })
            .catch((err) => { if (!cancelled) setError((err as Error).message); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Portal gate. Must run AFTER all hooks above to respect the rules of
    // hooks — returning null on the first render is safe because the
    // effects scheduled above will still fire and flip `mounted`, causing
    // a re-render that produces the portal.
    if (!mounted) return null;

    const loading = !error && docs === null;
    const hasDocs = !error && docs !== null && docs.length > 0;
    const emptyState = !error && docs !== null && docs.length === 0;

    const modalContent = (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="ضمان الأصالة"
            dir="rtl"
        >
            <div
                className="relative w-full max-w-xl max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <div className="flex items-start justify-between gap-3 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-white px-5 py-4">
                    <div>
                        <h2 className="text-lg font-bold text-emerald-900 flex items-center gap-2">
                            <span aria-hidden>🔒</span> ضمان الأصالة
                        </h2>
                        <p className="mt-1 text-xs text-emerald-800/80 leading-relaxed">
                            هذا المنتج مشمول ضمن نظام ضمان الأصالة لدينا.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="إغلاق"
                        className="shrink-0 rounded-full p-1.5 text-gray-500 hover:bg-gray-100"
                    >
                        ✕
                    </button>
                </div>

                {/* ── Premium Trust Summary Block (always shown) ── */}
                <div className="border-b border-emerald-50 bg-gradient-to-b from-white to-emerald-50/40 px-5 py-4">
                    <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                        {SUMMARY_CLAIMS.map((c) => (
                            <li
                                key={c.text}
                                className="flex items-center gap-2 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-xs font-semibold text-emerald-900 shadow-sm"
                            >
                                <span aria-hidden className="text-sm">{c.icon}</span>
                                <span>{c.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* ── Body ── */}
                <div className="overflow-y-auto max-h-[calc(85vh-232px)] px-5 py-4">
                    {error && (
                        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                            تعذّر تحميل الوثائق حالياً. {error}
                        </div>
                    )}

                    {loading && (
                        <div className="text-sm text-gray-500 py-6 text-center">جاري التحميل…</div>
                    )}

                    {/* State A — PDFs not uploaded yet. Premium finalisation
                        panel, intentionally not an error/empty look. */}
                    {emptyState && (
                        <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white px-4 py-4 shadow-sm">
                            <div className="flex items-start gap-3">
                                <div
                                    aria-hidden
                                    className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white text-base"
                                >
                                    🔒
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-sm font-bold text-emerald-900">
                                        مستندات التوثيق قيد التفعيل
                                    </h3>
                                    <p className="mt-1 text-xs text-emerald-900/80 leading-relaxed">
                                        كل منتجاتنا أصلية 100% ومصدرها مضمون.
                                        شهادات الاعتماد الرسمية للمورّدين تظهر هنا داخل المنصة
                                        فور اكتمال تفعيلها.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* State B — active documents. The inner <a href> PDF
                        links are safe HTML here because this entire subtree
                        is portal-rendered into document.body, outside any
                        parent <Link>/<a> that may wrap TrustBadge. */}
                    {hasDocs && (
                        <>
                            <p className="mb-3 text-xs text-emerald-900/80 leading-relaxed">
                                الوثائق التالية تدعم منظومة الأصالة المعتمدة لدينا.
                            </p>
                            <ul className="space-y-3">
                                {docs.map((d) => {
                                    const brands = Array.isArray(d.brandsCovered)
                                        ? d.brandsCovered.filter((s): s is string => typeof s === 'string' && s.length > 0)
                                        : [];
                                    return (
                                        <li
                                            key={d.id}
                                            className="rounded-xl border border-gray-200 p-3 hover:border-emerald-300 hover:bg-emerald-50/30 transition"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-semibold text-gray-900 truncate">{d.title}</div>
                                                    <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
                                                        {d.supplierName && <span>المورّد: {d.supplierName}</span>}
                                                        {d.issuerName && <span>الجهة المصدرة: {d.issuerName}</span>}
                                                        {d.issuedAt && (
                                                            <span>تاريخ الإصدار: {new Date(d.issuedAt).toLocaleDateString('ar-SA')}</span>
                                                        )}
                                                    </div>
                                                    {d.descriptionAr && (
                                                        <p className="mt-1.5 text-xs text-gray-700 leading-relaxed">{d.descriptionAr}</p>
                                                    )}
                                                    {brands.length > 0 && (
                                                        <ul className="mt-2 flex flex-wrap gap-1.5">
                                                            {brands.slice(0, 8).map((b) => (
                                                                <li
                                                                    key={b}
                                                                    className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800"
                                                                >
                                                                    {b}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                                <a
                                                    href={getPublicTrustFileUrl(d.id)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-700"
                                                >
                                                    عرض الوثيقة
                                                </a>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    // Portal target: document.body. The modal subtree (including its
    // <a href="…pdf"> links) is now mounted OUTSIDE any parent <Link>
    // wrapping the TrustBadge trigger — eliminating the nested-anchor
    // hydration error by DOM architecture rather than by rewriting
    // interaction semantics.
    return createPortal(modalContent, document.body);
}
